import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    LoaderContainer,
    ChannelCard,
    Icon,
    RequestLivestreamModal,
    AddLivestreamModal,
    StreamInfo,
    ChannelsOnboarding,
} from "ui/components";
import { Channel } from "core/models";
import { listChannels } from "core/services";
import { useSessionContext } from "ui/context/SessionContext";
import { requestChannel } from "core/services/site/channel";
import { useNotifications } from "ui/context/NotificationsContext";
import usePermissions from "ui/hooks/usePermissions";
import { paths } from "ui/routes";
import styles from "./Channels.module.scss";

const Channels: React.FC = () => {
    const userPermissions = usePermissions();
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const sessionContext = useSessionContext();
    const [projectChannels, setProjectChannels] = useState<
        Channel[] | undefined
    >(undefined);
    const [demoChannels, setDemoChannels] = useState<Channel[] | undefined>(
        undefined,
    );
    const [requestLivestreamModal, setRequestLivestreamModal] =
        useState<boolean>(false);
    const [addLivestreamModal, setAddLivestreamModal] =
        useState<boolean>(false);
    const [currentPlan, setCurrentPlan] = useState<string>();

    const getChannels = async (): Promise<Channel[]> => {
        if (!sessionContext.session || !sessionContext.selectedProject) {
            throw Error("Missing data for getChannels");
        }
        const channelResponse = await listChannels(
            sessionContext.session.token,
            sessionContext.selectedProject.id,
        );
        if (channelResponse.error || !channelResponse.value) {
            throw Error(
                channelResponse.error || "Error during listChannels call",
            );
        }
        return channelResponse.value;
    };

    const { data: channels, status } = useQuery({
        queryKey: ["channels"],
        queryFn: getChannels,
    });

    useEffect(() => {
        if (!channels || channels.length === 0) {
            return;
        }
        const userChannels: Channel[] = [];
        const spotOnDemoChannels: Channel[] = [];
        channels.forEach((c) => {
            if (!c.tags.includes("Demo")) {
                userChannels.push(c);
            } else {
                spotOnDemoChannels.push(c);
            }
        });
        setProjectChannels(userChannels);
        setDemoChannels(spotOnDemoChannels);
    }, [channels]);

    const userCanCreateClips = userPermissions.canCreateFragment;
    const userCanWatchLivestream = userPermissions.canWatchLive;
    const userIsProjectAdmin = userPermissions.isProjectAdmin;

    useEffect(() => {
        const timer = setInterval(
            () => queryClient.invalidateQueries({ queryKey: ["channels"] }),
            10000,
        );
        if (!sessionContext.selectedProject || !sessionContext.session) {
            return undefined;
        }
        setCurrentPlan(sessionContext.selectedProject.package);
        return () => {
            clearInterval(timer as ReturnType<typeof setInterval>);
        };
    }, [sessionContext.selectedProject, sessionContext.session]);

    const handleAddLivestream = async (message: string) => {
        try {
            if (!sessionContext.session || !sessionContext.selectedProject) {
                throw Error("no session or project");
            }
            const result = await requestChannel(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                message,
            );
            if (result.error) {
                throw Error(result.error);
            }
            addNotification(
                "success",
                "Livestream requested",
                "We will contact you",
                5000,
            );
        } catch (err) {
            addNotification("error", "Error", "Please try again", 0);
        }
        setRequestLivestreamModal(false);
    };

    if (!userCanCreateClips) {
        return (
            <div className={styles.page}>
                <div className={styles.heading}>Livestreams not available</div>
                <div className={styles.subheading}>
                    <NavLink
                        to={paths.settingsPlan}
                        className={styles.planLink}
                    >
                        Upgrade your plan
                    </NavLink>{" "}
                    to clip from livestreams
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageheader}>
                <div>
                    <div className={styles.heading}>Livestreams</div>
                    <div className={styles.subheading}>
                        Select your livestream you wish to clip from
                    </div>
                </div>
            </div>
            {status === "error" && (
                <div className={styles.error}>
                    Something went wrong while fetching your channels
                </div>
            )}
            {status === "pending" && (
                <LoaderContainer
                    message="Loading channels…"
                    className={styles.loading__indicator}
                />
            )}
            {status === "success" && (
                <>
                    <div className={styles.categoryheading}>
                        {projectChannels && projectChannels.length > 1
                            ? "Your livestreams"
                            : "Your livestream"}
                    </div>

                    {projectChannels && projectChannels.length >= 0 && (
                        <div className={styles.channels}>
                            {projectChannels
                                .slice()
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((channel) => (
                                    <ChannelCard
                                        key={channel.id}
                                        channel={channel}
                                        allowLivestreamPlayer={
                                            userCanWatchLivestream
                                        }
                                    />
                                ))}

                            {projectChannels.length === 0 &&
                                userIsProjectAdmin && (
                                    <div
                                        role="presentation"
                                        className={styles.addchannel}
                                        onClick={() =>
                                            setRequestLivestreamModal(true)
                                        }
                                    >
                                        <div
                                            className={
                                                styles.addchannel__content
                                            }
                                        >
                                            <Icon
                                                name="email"
                                                className={
                                                    styles.addchannel__icon
                                                }
                                            />
                                            <span
                                                className={
                                                    styles.addchannel__text
                                                }
                                            >
                                                Get in touch to connect your own
                                                live stream
                                            </span>
                                        </div>
                                    </div>
                                )}

                            {userIsProjectAdmin &&
                                projectChannels.length > 0 && (
                                    <div
                                        role="presentation"
                                        className={styles.addchannel}
                                        onClick={() =>
                                            setRequestLivestreamModal(true)
                                        }
                                    >
                                        <div
                                            className={
                                                styles.addchannel__content
                                            }
                                        >
                                            <Icon
                                                name="email"
                                                className={
                                                    styles.addchannel__icon
                                                }
                                            />
                                            <span
                                                className={
                                                    styles.addchannel__text
                                                }
                                            >
                                                Require more streams? Contact us
                                            </span>
                                        </div>
                                    </div>
                                )}

                            {projectChannels.length === 0 &&
                                !userIsProjectAdmin && (
                                    <div
                                        role="presentation"
                                        className={styles.checkwithadmin}
                                    >
                                        No stream? Check with your admin
                                    </div>
                                )}
                        </div>
                    )}

                    {currentPlan !== "custom" &&
                        projectChannels &&
                        projectChannels.length > 0 && (
                            <StreamInfo projectChannels={projectChannels} />
                        )}

                    {demoChannels && demoChannels.length > 0 && (
                        <div>
                            <div className={styles.channelsHorizontalLine} />
                            <div className={styles.categoryheading}>
                                SpotOn demo streams
                            </div>
                            <div className={styles.channels}>
                                {demoChannels.map((channel) => (
                                    <ChannelCard
                                        key={channel.id}
                                        channel={channel}
                                        allowLivestreamPlayer={
                                            userCanWatchLivestream
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
            <ChannelsOnboarding />

            {addLivestreamModal && (
                <AddLivestreamModal
                    onClose={() => setAddLivestreamModal(false)}
                />
            )}

            {requestLivestreamModal && (
                <RequestLivestreamModal
                    onAdd={handleAddLivestream}
                    onClose={() => setRequestLivestreamModal(false)}
                />
            )}
        </div>
    );
};

export default Channels;
